import { useState } from "react";
// La regola su cosa è un segreto è quella del dominio: una seconda definizione
// nel browser divergerebbe dal server al primo indizio aggiunto.
import { isSensitiveKey } from "../../server/domain/env/env-file.ts";
import { azionePer, conseguenzaDi, type Azione } from "../lib/azioni-verifica.ts";
import { contaRisolti, haValore, problemaRisolto, segnaposto } from "../lib/risoluzione.ts";
import type {
  DeployStrategy,
  Issue,
  IssueCode,
  ServiceReport,
  StackValidation,
  VariableReport,
} from "../lib/types.ts";
import { Badge, Button, Card, cx } from "./ui.tsx";
import { useT } from "../lib/i18n.tsx";
import type { Chiave } from "../../i18n/index.ts";

/**
 * Esito della verifica di uno stack.
 *
 * La domanda che porta qui è una sola: posso avviare, e se no perché. Da lì
 * l'ordine — verdetto, cosa impedisce l'avvio, cosa accadrà — e il fatto che
 * ogni problema porti con sé il gesto che lo risolve invece di lasciare il
 * lettore a cercarlo.
 */

/**
 * Il necessario per correggere una variabile senza lasciare la verifica.
 *
 * Viaggia come un blocco unico: o si può correggere, e allora serve tutto, o non
 * si può, e allora è `null`. Passare i pezzi singolarmente moltiplicherebbe i
 * controlli in ogni componente attraversato.
 */
interface Correzione {
  readonly env: Record<string, string>;
  readonly onImposta: (nome: string, valore: string) => void;
  readonly segretiVisibili: boolean;
  readonly onRivela?: () => void;
  readonly cambiate: readonly string[];
}

export interface PannelloValidazioneProps {
  report: StackValidation;
  /** Strategia dello stack: determina cosa accadrà all'avvio. */
  strategia?: DeployStrategy;
  onAzione?: (azione: Azione) => void;
  onRicontrolla?: () => void;
  /** Environment in corso di modifica: contiene anche ciò che non è ancora salvato. */
  env?: Record<string, string>;
  /** Assente quando non si ha il permesso di scrivere: i campi restano in lettura. */
  onImposta?: (nome: string, valore: string) => void;
  /** Variabili che differiscono dall'ultimo salvataggio. */
  variabiliCambiate?: readonly string[];
  segretiVisibili?: boolean;
  onRivela?: () => void;
  salvataggio?: { esegui: () => void; inCorso: boolean };
}

export function PannelloValidazione({
  report,
  strategia = "recreate",
  onAzione,
  onRicontrolla,
  env,
  onImposta,
  variabiliCambiate = [],
  segretiVisibili = false,
  onRivela,
  salvataggio,
}: PannelloValidazioneProps) {
  // Le correzioni fatte da qui si vedono subito, senza attendere il server: il
  // problema si chiude mentre lo si risolve. Resta un'anticipazione, e la barra
  // in fondo lo dichiara chiedendo di salvare per confermarla.
  const correzione: Correzione | null =
    env && onImposta
      ? { env, onImposta, segretiVisibili, onRivela, cambiate: variabiliCambiate }
      : null;

  // L'immagine di ogni servizio: serve alle azioni per portare all'`image:`
  // giusto invece che al primo del file.
  const immagineDi = (servizio: string) =>
    report.services.find((s) => s.name === servizio)?.image ?? null;

  const risolti = env ? contaRisolti([...report.errors, ...report.warnings], env) : 0;
  const t = useT();

  return (
    <div className="space-y-4">
      <Verdetto report={report} risolti={risolti} onRicontrolla={onRicontrolla} />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          {report.errors.length > 0 && (
            <ElencoProblemi
              titolo={t.t("verifica.daRisolvere")}
              tono="errore"
              problemi={report.errors}
              onAzione={onAzione}
              immagineDi={immagineDi}
              correzione={correzione}
            />
          )}

          {report.nameConflicts.length > 0 && <ConflittiNomi report={report} />}

          {report.portConflicts.length > 0 && <ConflittiPorte report={report} />}

          {report.warnings.length > 0 && (
            <ElencoProblemi
              titolo={t.t("verifica.daValutare")}
              tono="avviso"
              problemi={report.warnings}
              onAzione={onAzione}
              immagineDi={immagineDi}
              correzione={correzione}
            />
          )}

          {/* I valori stanno a sinistra, con i problemi: è la colonna di ciò su
              cui si agisce, mentre a destra si legge cosa accadrà. Prima erano in
              fondo a destra, dove non li cercava nessuno. */}
          <Variabili variabili={report.variables} onAzione={onAzione} correzione={correzione} />
        </div>

        <div className="space-y-4">
          <RiepilogoAvvio report={report} strategia={strategia} />
        </div>
      </div>

      {correzione && salvataggio && (
        <BarraCorrezioni correzione={correzione} risolti={risolti} salvataggio={salvataggio} />
      )}
    </div>
  );
}

function Verdetto({
  report,
  risolti,
  onRicontrolla,
}: {
  report: StackValidation;
  /** Problemi già chiusi dalle correzioni in corso, non ancora salvate. */
  risolti: number;
  onRicontrolla?: () => void;
}) {
  // Porte e nomi occupati bloccano quanto gli errori del compose: senza contarli
  // il verdetto direbbe "pronto" mentre l'avvio si fermerebbe comunque.
  const bloccanti = Math.max(
    0,
    report.errors.length + report.portConflicts.length + report.nameConflicts.length - risolti,
  );
  const pronto = bloccanti === 0;
  // Pronto grazie a correzioni non salvate: dirlo "pronto per l'avvio" sarebbe
  // falso, perché avviare userebbe l'ultima revisione salvata.
  // `deployable` è il giudizio del server su ciò che è salvato: se dice no
  // mentre qui non restano bloccanti, la differenza sono proprio le correzioni
  // ancora da salvare.
  const inAttesaDiSalvataggio = pronto && !report.deployable;
  const t = useT();

  // Tre stati, non due: "risolto ma non salvato" non è né un blocco né un
  // via libera, e mostrarlo verde pieno inviterebbe ad avviare troppo presto.
  const stile = !pronto
    ? {
        cornice: "border-red-500/30 bg-red-500/8",
        cerchio: "bg-red-500/20 text-red-500",
        testo: "text-red-700 dark:text-red-400",
        segno: "!",
      }
    : inAttesaDiSalvataggio
      ? {
          cornice: "border-amber-500/30 bg-amber-500/8",
          cerchio: "bg-amber-500/20 text-amber-500",
          testo: "text-amber-700 dark:text-amber-400",
          segno: "→",
        }
      : {
          cornice: "border-emerald-500/30 bg-emerald-500/8",
          cerchio: "bg-emerald-500/20 text-emerald-500",
          testo: "text-emerald-700 dark:text-emerald-400",
          segno: "✓",
        };

  return (
    <div
      className={cx("flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3", stile.cornice)}
    >
      <span
        className={cx(
          "flex size-7 shrink-0 items-center justify-center rounded-full",
          stile.cerchio,
        )}
        aria-hidden
      >
        {stile.segno}
      </span>

      <div className="min-w-0">
        <p className={cx("font-medium", stile.testo)}>
          {!pronto
            ? t.t("verifica.bloccanti", { n: bloccanti })
            : t.t(inAttesaDiSalvataggio ? "verifica.prontoDopoSalvataggio" : "verifica.pronto")}
        </p>
        <p className="text-xs tenue">
          {!pronto
            ? t.t("verifica.siFermerebbe")
            : inAttesaDiSalvataggio
              ? t.t("verifica.usaUltimaRevisione")
              : report.warnings.length > 0
                ? t.t("verifica.avvisi", { n: report.warnings.length })
                : t.t("verifica.superati")}
        </p>
      </div>

      {onRicontrolla && (
        <Button className="ml-auto text-xs" onClick={onRicontrolla}>
          {t.t("verifica.ricontrolla")}
        </Button>
      )}
    </div>
  );
}

function ElencoProblemi({
  titolo,
  tono,
  problemi,
  onAzione,
  immagineDi,
  correzione,
}: {
  titolo: string;
  tono: "errore" | "avviso";
  problemi: readonly Issue[];
  onAzione?: (azione: Azione) => void;
  immagineDi: (servizio: string) => string | null;
  correzione: Correzione | null;
}) {
  const aperti = correzione
    ? problemi.length - contaRisolti(problemi, correzione.env)
    : problemi.length;
  const t = useT();

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">
        {titolo}{" "}
        {/* A zero il numero non dice nulla, mentre le righe barrate restano a
            schermo: meglio nominare il fatto che sono state risolte tutte. */}
        <span className="tenue">
          {aperti === 0 && problemi.length > 0 ? `· ${t.t("verifica.tuttiRisolti")}` : `· ${aperti}`}
        </span>
      </h3>

      <Card className="divide-y overflow-hidden">
        {problemi.map((problema, indice) => (
          <RigaProblema
            key={indice}
            problema={problema}
            tono={tono}
            onAzione={onAzione}
            immagineDi={immagineDi}
            correzione={correzione}
          />
        ))}
      </Card>
    </section>
  );
}

function RigaProblema({
  problema,
  tono,
  onAzione,
  immagineDi,
  correzione,
}: {
  problema: Issue;
  tono: "errore" | "avviso";
  onAzione?: (azione: Azione) => void;
  immagineDi: (servizio: string) => string | null;
  correzione: Correzione | null;
}) {
  const t = useT();
  const azione = azionePer(problema, problema.service ? immagineDi(problema.service) : null);
  const conseguenza = conseguenzaDi(problema.code as IssueCode, t);

  const risolto = correzione ? problemaRisolto(problema, correzione.env) : false;

  // Una variabile mancante si compila qui: è il gesto che chiude il problema, e
  // mandarlo in un'altra scheda vuol dire perdere di vista perché lo si faceva.
  const compilabile =
    correzione !== null && problema.code === "MISSING_VARIABLE" && Boolean(problema.variable);

  return (
    <div className={cx("px-4 py-3", risolto && "bg-emerald-500/5")}>
      <div className="flex flex-wrap items-start gap-3">
        <span
          className={cx(
            "mt-1.5 size-2 shrink-0 rounded-full",
            risolto ? "bg-emerald-500" : tono === "errore" ? "bg-red-500" : "bg-amber-500",
          )}
          aria-hidden
        />

        <div className="min-w-0 flex-1 space-y-1">
          <p className={cx("text-sm", risolto && "tenue line-through")}>
            {titoloProblema(problema, t)}
          </p>

          {risolto ? (
            <p className="text-xs text-emerald-600 dark:text-emerald-400">{t.t("verifica.risolto")}</p>
          ) : (
            conseguenza && <p className="text-xs tenue">{conseguenza}</p>
          )}

          {/* Il testo completo resta disponibile: contiene il motivo per esteso,
              che a volte è ciò che serve per capire davvero. */}
          {!risolto && (
            <details className="text-xs">
              <summary className="cursor-pointer tenue hover:text-[var(--testo)]">
                {t.t("verifica.dettagli")}
              </summary>
              <p className="mt-1 tenue">{t.traduci(problema.messaggio) ?? problema.message}</p>
            </details>
          )}
        </div>

        {/* Il salto al compose resta un pulsante: là si modifica il testo, non un
            valore, e non ha senso ridurlo a un campo. */}
        {!compilabile && !risolto && azione && onAzione && (
          <Button className="shrink-0 text-xs" onClick={() => onAzione(azione)}>
            {t.t(azione.etichetta)} →
          </Button>
        )}
      </div>

      {compilabile && (
        <CampoVariabile
          nome={problema.variable!}
          correzione={correzione!}
          className="mt-2 pl-5"
        />
      )}
    </div>
  );
}

/**
 * Campo con cui impostare una variabile dove il problema la nomina.
 *
 * Il valore va nella bozza condivisa, la stessa che modifica l'editor
 * dell'environment: non esiste un secondo posto in cui possa divergere.
 */
function CampoVariabile({
  nome,
  correzione,
  className,
  compatto = false,
  testoSegnaposto,
}: {
  nome: string;
  correzione: Correzione;
  className?: string;
  compatto?: boolean;
  testoSegnaposto?: string;
}) {
  const t = useT();
  const valore = correzione.env[nome] ?? "";
  const sensibile = isSensitiveKey(nome);

  // Un valore sensibile già presente arriva mascherato dal server: sovrascriverlo
  // senza vederlo significherebbe salvare i puntini al posto del segreto.
  const bloccatoDaiSegreti = sensibile && !correzione.segretiVisibili && valore !== "";

  if (bloccatoDaiSegreti) {
    return (
      <div className={cx("flex items-center gap-2 text-xs tenue", className)}>
        <span className="font-mono">{nome}</span>
        <span>{t.t("verifica.valoreNascosto")}</span>
        {correzione.onRivela && (
          <button
            type="button"
            onClick={correzione.onRivela}
            className="underline hover:text-[var(--testo)]"
          >
            {t.t("verifica.mostraPerModificare")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={cx("flex items-center gap-2", className)}>
      {!compatto && (
        <label htmlFor={`var-${nome}`} className="shrink-0 font-mono text-xs tenue">
          {nome}
        </label>
      )}

      <input
        id={`var-${nome}`}
        value={valore}
        onChange={(evento) => correzione.onImposta(nome, evento.target.value)}
        placeholder={testoSegnaposto ?? t.t("verifica.segnaposto")}
        spellCheck={false}
        autoComplete="off"
        aria-label={t.t("verifica.valoreDi", { nome })}
        className="min-w-0 flex-1 rounded border bg-[var(--sfondo)] px-2 py-1 font-mono text-xs outline-none focus:border-accent-600"
      />
    </div>
  );
}

/** Titolo breve, che dice il fatto invece di spiegarlo. */
function titoloProblema(problema: Issue, t: ReturnType<typeof useT>): string {
  return (
    t.traduci({
      chiave: `verifica.titolo.${problema.code}`,
      parametri: { variabile: problema.variable ?? "", servizio: problema.service ?? "" },
    }) ?? problema.message
  );
}

/**
 * Nomi di container già presi da altri.
 *
 * Merita un riquadro proprio, e non una riga fra gli errori, perché la causa non
 * sta nel compose ma fuori: sull'host c'è già qualcosa che occupa quel nome, e
 * la via d'uscita dipende da che cosa sia — spesso i resti di un altro
 * orchestratore, che nessun controllo sul file potrebbe rivelare.
 */
function ConflittiNomi({ report }: { report: StackValidation }) {
  const t = useT();
  return (
    <Card className="border-red-500/30 p-4">
      <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">
        {t.t("verifica.nomiOccupati")}
      </h3>

      <ul className="mt-2 space-y-1.5 text-sm">
        {report.nameConflicts.map((conflitto) => (
          <li key={conflitto.name}>
            {t.t("verifica.nomeOccupato", {
              nome: conflitto.name,
              servizio: conflitto.service,
              stato: conflitto.state === "running" ? t.t("stacks.inEsecuzione") : conflitto.state,
              progetto: conflitto.project,
            })}
          </li>
        ))}
      </ul>

      <p className="mt-2.5 text-xs tenue">{t.t("verifica.nomiSpiegazione")}</p>
    </Card>
  );
}

function ConflittiPorte({ report }: { report: StackValidation }) {
  const t = useT();
  return (
    <Card className="border-red-500/30 p-4">
      <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">
        {t.t("verifica.porteOccupate")}
      </h3>

      <ul className="mt-2 space-y-1.5 text-sm">
        {report.portConflicts.map((conflitto) => (
          <li key={`${conflitto.port}/${conflitto.protocol}`}>
            {t.t("verifica.portaOccupata", { porta: conflitto.port, da: conflitto.heldBy })}
            {conflitto.suggestion !== null && ` ${t.t("verifica.portaLibera", { porta: conflitto.suggestion })}`}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Attesa di indisponibilità dichiarata per ciascuna strategia. */
const INTERRUZIONE: Record<DeployStrategy, Chiave> = {
  recreate: "verifica.interruzioneRicreazione",
  "blue-green": "verifica.interruzioneBlueGreen",
};

function RiepilogoAvvio({
  report,
  strategia,
}: {
  report: StackValidation;
  strategia: DeployStrategy;
}) {
  const porteDaAssegnare = report.autoPorts.length;
  const t = useT();

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{t.t("verifica.cosaAccadra")}</h3>

      <Card className="divide-y overflow-hidden">
        {report.services.map((servizio) => (
          <RigaServizio key={servizio.name} servizio={servizio} />
        ))}
      </Card>

      <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
        <dt className="tenue">{t.t("verifica.immagini")}</dt>
        <dd>{report.images.length}</dd>

        <dt className="tenue">{t.t("verifica.porteAssegnate")}</dt>
        <dd>{porteDaAssegnare === 0 ? t.t("verifica.nessuna") : porteDaAssegnare}</dd>

        <dt className="tenue">{t.t("verifica.interruzione")}</dt>
        <dd>{t.t(INTERRUZIONE[strategia])}</dd>
      </dl>
    </section>
  );
}

function RigaServizio({ servizio }: { servizio: ServiceReport }) {
  const porte = servizio.ports.filter((porta) => porta.published !== null);
  const t = useT();

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
      <span className="font-medium">{servizio.name}</span>

      {servizio.problemi.includes("MISSING_TAG") || servizio.problemi.includes("MISSING_IMAGE") ? (
        <Badge tone="danger">{t.t("verifica.daCorreggere")}</Badge>
      ) : servizio.problemi.length > 0 ? (
        <Badge tone="warning">{t.t("verifica.daValutareBreve")}</Badge>
      ) : null}

      <code className="min-w-0 flex-1 truncate font-mono text-xs tenue">
        {servizio.image ?? t.t("verifica.nessunaImmagine")}
      </code>

      <span className="shrink-0 font-mono text-xs">
        {porte.length === 0 ? (
          <span className="tenue">{t.t("verifica.soloInterna")}</span>
        ) : (
          porte.map((porta) => (
            <span key={`${porta.published}-${porta.target}`} className="ml-1.5">
              {porta.managed ? (
                <span className="text-accent-600 dark:text-accent-500" title={t.t("verifica.assegnataDaPortman")}>
                  {t.t("verifica.auto")}
                </span>
              ) : (
                porta.published
              )}
              <span className="tenue">→{porta.target}</span>
            </span>
          ))
        )}
      </span>

      {!servizio.hasHealthcheck && (
        <span className="shrink-0 text-xs tenue" title={t.t("verifica.senzaHealthcheckAiuto")}>
          {t.t("verifica.senzaHealthcheck")}
        </span>
      )}
    </div>
  );
}

const STATI: Record<VariableReport["status"], { testo: Chiave; tono: Parameters<typeof Badge>[0]["tone"] }> = {
  provided: { testo: "verifica.statoImpostata", tono: "success" },
  default: { testo: "verifica.statoPredefinito", tono: "neutral" },
  managed: { testo: "verifica.statoPortman", tono: "accent" },
  missing: { testo: "verifica.statoMancante", tono: "danger" },
};

function Variabili({
  variabili,
  onAzione,
  correzione,
}: {
  variabili: readonly VariableReport[];
  onAzione?: (azione: Azione) => void;
  correzione: Correzione | null;
}) {
  const mancanti = correzione
    ? variabili.filter((v) => v.status === "missing" && !haValore(correzione.env[v.name])).length
    : variabili.filter((v) => v.status === "missing").length;

  // Aperto quando i valori si possono modificare: chiuso, la possibilità non si
  // vede, e un pannello che nasconde l'unica cosa su cui si può agire diventa
  // una funzione che esiste senza che nessuno la trovi.
  const [aperto, setAperto] = useState(correzione !== null || mancanti > 0);
  const t = useT();

  if (variabili.length === 0) return null;

  return (
    <section className="space-y-2">
      <button
        onClick={() => setAperto(!aperto)}
        className="flex w-full items-center gap-2 text-sm font-semibold"
      >
        <span>{t.t(correzione ? "verifica.valoriEnvironment" : "verifica.variabiliCompose")}</span>
        <span className="tenue">· {variabili.length}</span>
        {mancanti > 0 && <Badge tone="danger">{t.t("verifica.mancanti", { n: mancanti })}</Badge>}
        <span className="ml-auto text-xs tenue">{t.t(aperto ? "comune.nascondi" : "comune.mostra")}</span>
      </button>

      {aperto && correzione && (
        <p className="text-xs tenue">{t.t("verifica.modificaliQui")}</p>
      )}

      {aperto && (
        <Card className="divide-y overflow-hidden">
          {variabili.map((variabile) => {
            // Le variabili gestite da Portman restano in sola lettura: la porta
            // host la assegna lui, e offrirne la modifica inviterebbe a
            // combattere con l'allocatore.
            const compilabile = correzione !== null && variabile.status !== "managed";
            const cambiata = correzione?.cambiate.includes(variabile.name) ?? false;

            return (
              <div key={variabile.name} className={cx("px-3 py-1.5", cambiata && "bg-amber-500/5")}>
                <div className="flex items-center gap-2 text-sm">
                  <code className="min-w-0 flex-1 truncate font-mono text-[13px]">
                    {variabile.name}
                  </code>

                  {cambiata ? (
                    <Badge tone="warning">{t.t("verifica.daSalvare")}</Badge>
                  ) : (
                    <Badge tone={STATI[variabile.status].tono}>
                      {t.t(STATI[variabile.status].testo)}
                    </Badge>
                  )}

                  {/* Senza permesso di scrivere resta il salto all'environment,
                      dove il valore si legge anche solo per guardarlo. */}
                  {!compilabile && variabile.status === "missing" && onAzione && (
                    <Button
                      className="shrink-0 text-xs"
                      onClick={() =>
                        onAzione({
                          tipo: "imposta-variabile",
                          etichetta: "verifica.imposta",
                          variabile: variabile.name,
                        })
                      }
                    >
                      {t.t("verifica.imposta")}
                    </Button>
                  )}
                </div>

                {compilabile && (
                  <CampoVariabile
                    nome={variabile.name}
                    correzione={correzione!}
                    className="mt-1"
                    compatto
                    testoSegnaposto={segnaposto(variabile, t)}
                  />
                )}
              </div>
            );
          })}
        </Card>
      )}
    </section>
  );
}

/**
 * Barra delle correzioni in corso.
 *
 * Sta in fondo perché è l'ultimo passo, e dice due cose distinte che è facile
 * confondere: le modifiche vanno salvate, e salvare non avvia nulla.
 */
function BarraCorrezioni({
  correzione,
  risolti,
  salvataggio,
}: {
  correzione: Correzione;
  risolti: number;
  salvataggio: { esegui: () => void; inCorso: boolean };
}) {
  const quante = correzione.cambiate.length;
  const t = useT();

  if (quante === 0) return null;

  return (
    <div className="sticky bottom-3 flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 backdrop-blur">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">
          {t.t("verifica.modificate", { n: quante })}
          {risolti > 0 && `, ${t.t("verifica.risolti", { n: risolti })}`}
        </p>
        <p className="text-xs tenue">{t.t("verifica.salvareNonAvvia")}</p>
      </div>

      <Button
        variant="primary"
        loading={salvataggio.inCorso}
        onClick={salvataggio.esegui}
        className="shrink-0"
      >
        {t.t("verifica.salvaRicontrolla")}
      </Button>
    </div>
  );
}
